import React, {useEffect, useState} from "react";
import classNames from "classnames";
import {observer} from "mobx-react-lite";


import {useAppState} from "@stores/AppStateContext";
import {useFileProvider} from "@stores/AppStateContext";
import {DeleteIcon} from "../icons/DeleteIcon";
import {AddIcon} from "../icons/AddIcon";

import {MODALS} from "../../screens/modals/modal-names";


import "./ImageGalleryPreview.css";


/**
 * this component expects ether already Existing files in witchCase it path/thumbPath,
 * or newly uploaded in form input files, then it's just src =
 */
export interface UrlWithPreview {
    path?: string,
    thumbPath?: string,
    src?: string,
}

export interface ImageGalleryPreviewProps {
    urls: UrlWithPreview[];
    onAdd?: () => void;
    onRemove?: (index: number) => void;
    maxVisible?: number;
    className?: string;
}

export const ImageGalleryPreview = observer((
    {
        urls,
        onRemove,
        onAdd,
        maxVisible = 5,
        className,
    }: ImageGalleryPreviewProps
) => {
    const appState = useAppState();
    const fileProvider = useFileProvider();
    const [previewImages, setPreviewImages] = useState(urls.map((url, index) => ({src: url.src, index})));

    useEffect(() => {
        const _previewSrcs = [];
        if (urls.some(url => url.thumbPath) && fileProvider) {
            (async () => {
                let i = 0;
                for (const url of urls) {
                    if (url.thumbPath) {
                        _previewSrcs.push({src: await fileProvider.getSignedUrl(url.thumbPath), index: i++});
                    } else {
                        _previewSrcs.push({src: url.src, index: i++});
                    }
                }
                setPreviewImages(_previewSrcs);
            })()
        }
    }, [urls]);

    const hasOverflow = previewImages.length > maxVisible;
    const overflowCount = previewImages.length - (maxVisible - 1);

    const displayImages = previewImages.slice(0, maxVisible - (hasOverflow ? 1 : 0));
    const overflowImage = hasOverflow ? previewImages[maxVisible - 1] : null;

    const openLightbox = async (startIndex: number) => {
        const images = (await Promise.all(
            urls.map(async url => {
                if (url.src) {
                    return {src: url.src};
                } else if (url.path) {
                    return {src: await fileProvider!.getSignedUrl(url.path)}
                }
                return null;
            })
        )).filter(value => !!value);
        appState.setModal({
            name: MODALS.LIGHTBOX,
            params: {
                images,
                initialIndex: startIndex,
                onRemove: onRemove,
                onClose: () => appState.setModal(null),
            },
        });
    };

    useEffect(() => {
        if (appState.topModal === MODALS.LIGHTBOX) {
            openLightbox(0);
        }
    }, [urls, appState.topModal]);

    return (
        <div className={classNames({
            "ImageGalleryPreview": true,
            [className!]: className
        })}
        >
            {onAdd && (
                <div className="ImageGalleryPreview__Item ImageGalleryPreview__AddNew"
                     onClick={onAdd}
                >
                    <AddIcon className="ImageGalleryPreview__AddIcon"/>
                </div>
            )}
            {displayImages.map(({src, index}) => (
                <div key={index} className="ImageGalleryPreview__Item">
                    <img
                        src={src}
                        alt="preview"
                        className="ImageGalleryPreview__Image"
                        onClick={() => openLightbox(index)}
                    />
                    {onRemove && (
                        <DeleteIcon
                            additionalPadding={8}
                            className="ImageGalleryPreview__RemoveButton theme-dark"
                            onClick={() => onRemove(index)}
                        />
                    )}
                </div>
            ))}
            {overflowImage && (
                <div className="ImageGalleryPreview__Item"
                     onClick={() => openLightbox(maxVisible - 1)}
                >
                    <img
                        src={overflowImage.src}
                        alt="overflow"
                        className="ImageGalleryPreview__Image"
                    />
                    <span className="headline-small ImageGalleryPreview__ExtraOverlay theme-dark">
                        +{overflowCount}
                    </span>
                </div>
            )}
        </div>
    );
});
